// Structured data + <head> management for the whole site.
//
// Two audiences, two formats:
//   • JSON-LD (this file, injected per route) — Google Search rich results:
//     TravelAgency organisation, WebSite, BreadcrumbList, Hotel / VacationRental
//     entities with ratings, images and amenities, Guide articles, ItemLists.
//   • Microdata (see site/HotelMicrodata.jsx) — Google Hotel Center price
//     accuracy validation, which reads itemscope/itemprop on the landing page
//     and the final pre-payment page. JSON-LD is deprecated for that check.
//
// Identity rule: hotels are typed Hotel; self-catering rentals are typed
// Hotel + VacationRental; the site is a TravelAgency (online travel agency);
// editorial pages are Article + Guide (never BlogPosting).

import { useEffect } from "react";
import { lodgingKind, lodgingLabel, lodgingSchemaTypes, lodgingAdditionalType, typeLabel } from "./hotelTypes.js";

export const SITE = {
  name: "Aloha Rates",
  origin: "https://aloharates.com",
  logo: "https://home.aloharates.com/home_page/Untitled%20-%20December%2010%2C%202025%20at%2016.40.27%C2%A0(1).png",
  description: "Aloha Rates is an online travel agency for Hawaiʻi. Book hotels, resorts and vacation rentals on Oʻahu, Maui, Kauaʻi, Hawaiʻi Island, Molokaʻi and Lānaʻi at real local rates with taxes included.",
  // Hawaiʻi Standard Time, no daylight saving.
  utcOffset: "-10:00"
};
export const ORG_ID = `${SITE.origin}/#organization`;
export const WEBSITE_ID = `${SITE.origin}/#website`;
export const CONTEXT = "https://schema.org";

export const absUrl = path => String(path || "/").startsWith("http") ? path : `${SITE.origin}${String(path || "/").startsWith("/") ? "" : "/"}${path || "/"}`;
export const slugify = v => String(v || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
export const propertyPath = p => `/properties/${p.id}-${slugify(p.name)}`;
export const stripHtml = s => String(s || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim();
export const clip = (s, n = 300) => { const t = stripHtml(s); return t.length > n ? `${t.slice(0, n - 1).trimEnd()}…` : t; };

// Compact references to the site-wide nodes declared statically in index.html.
export const orgRef = () => ({ "@type": "TravelAgency", "@id": ORG_ID, name: SITE.name, url: `${SITE.origin}/`, logo: { "@type": "ImageObject", url: SITE.logo } });
export const websiteRef = () => ({ "@type": "WebSite", "@id": WEBSITE_ID, name: SITE.name, url: `${SITE.origin}/` });
export const hawaiiPlace = () => ({ "@type": "State", name: "Hawaiʻi", alternateName: "Hawaii", address: { "@type": "PostalAddress", addressRegion: "HI", addressCountry: "US" } });

export const ISLAND_NAMES = {
  "oahu": "Oʻahu", "maui": "Maui", "kauai": "Kauaʻi", "big-island": "Hawaiʻi Island", "molokai": "Molokaʻi", "lanai": "Lānaʻi"
};
const ISLAND_SLUG_BY_DATA_NAME = { "Oahu": "oahu", "Maui": "maui", "Kauai": "kauai", "Big Island": "big-island", "Molokai": "molokai", "Lanai": "lanai" };
export const islandSlugFor = p => ISLAND_SLUG_BY_DATA_NAME[p?.island] || slugify(p?.island || "");
export const islandDisplayFor = p => ISLAND_NAMES[islandSlugFor(p)] || p?.island || "";

export function breadcrumbNode(items) {
  return {
    "@context": CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: absUrl(it.path) }))
  };
}

// "04:00 PM" → "16:00:00-10:00" (ISO 8601 time with the Hawaiʻi offset).
export function toIsoTime(t) {
  const m = String(t || "").trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!m) return undefined;
  let h = Number(m[1]); const min = m[2] || "00"; const ap = (m[3] || "").toUpperCase();
  if (ap === "PM" && h < 12) h += 12;
  if (ap === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${min}:00${SITE.utcOffset}`;
}
export const checkinTimes = p => {
  const t = p?.checkinCheckoutTimes || {};
  return { checkin: toIsoTime(t.checkin_start || t.checkinStart), checkout: toIsoTime(t.checkout || t.checkoutEnd) };
};

// Hotel Center wants every PostalAddress sub-field once you emit an address.
export function postalAddress(p) {
  const raw = String(p?.address || "").trim();
  const city = String(p?.city || "").trim();
  // Supplier zip fields arrive as "96761" or "HI 96761"; keep the 5-digit code.
  const zip = String(p?.zip || "").match(/\b(\d{5})\b/)?.[1] || raw.match(/\b(\d{5})(?:-\d{4})?\b/)?.[1] || undefined;
  let street = raw;
  if (city && raw.toLowerCase().includes(city.toLowerCase())) {
    street = raw.slice(0, raw.toLowerCase().indexOf(city.toLowerCase())).replace(/[,\s]+$/, "");
  } else {
    street = raw.replace(/,\s*(Hawaii|HI)\b.*$/i, "").replace(/,\s*\d{5}(-\d{4})?\s*$/, "").trim();
  }
  const node = { "@type": "PostalAddress", addressRegion: "HI", addressCountry: "US" };
  if (street) node.streetAddress = street;
  if (city) node.addressLocality = city;
  if (zip) node.postalCode = zip;
  return node;
}

export const geoNode = p => {
  const lat = p?.location?.latitude ?? p?.lat, lng = p?.location?.longitude ?? p?.lng;
  return Number.isFinite(lat) && Number.isFinite(lng) ? { "@type": "GeoCoordinates", latitude: lat, longitude: lng } : undefined;
};

// Supplier ratings are on a 0–10 scale. Only emit when there are real reviews.
export const aggregateRatingNode = p => (p?.rating > 0 && p?.reviewCount > 0)
  ? { "@type": "AggregateRating", ratingValue: Number(Number(p.rating).toFixed(1)), bestRating: 10, worstRating: 1, reviewCount: Number(p.reviewCount) }
  : undefined;

export function lodgingImages(p, limit = 12) {
  const hd = (p?.hotelImages || []).map(i => i?.urlHd || i?.url).filter(Boolean);
  if (hd.length) return hd.slice(0, limit);
  const out = [];
  if (p?.mainPhoto || p?.main_photo) out.push(p.mainPhoto || p.main_photo);
  out.push(`https://images.aloharates.com/properties/${p?.id}/0.webp`);
  return out.slice(0, limit);
}

// Google's amenityFeature vocabulary (vacation-rental + hotel docs share names).
const FACILITY_FLAGS = {
  hotTub: [63], gymFitnessEquipment: [492, 723, 537], outdoorGrill: [72, 983, 984],
  fireplace: [225, 509, 1979], airportShuttle: [17, 140, 594]
};
export function amenityFeatures(p) {
  const out = [];
  const add = (name, value) => out.push({ "@type": "LocationFeatureSpecification", name, value });
  if (p?.hasPool) add("pool", true);
  if (p?.hasWifi) add("wifi", true);
  if (p?.hasKitchen) add("kitchen", true);
  if (p?.hasBeachfront) add("beachAccess", true);
  if (p?.hasParking) add("parkingType", "Free");
  if (p?.hasPetsAllowed || p?.petsAllowed) add("petsAllowed", true);
  if (p?.childAllowed === true) add("childFriendly", true);
  const ids = p?.facilityIds || [];
  for (const [name, list] of Object.entries(FACILITY_FLAGS)) if (ids.some(id => list.includes(id))) add(name, true);
  return out;
}

const BED_WORDS = [
  [/california\s*king/i, "CaliforniaKing"], [/king/i, "King"], [/queen/i, "Queen"],
  [/double/i, "Double"], [/full/i, "Full"], [/twin|single/i, "Single"]
];
export const bedType = raw => { const s = String(raw || ""); for (const [re, v] of BED_WORDS) if (re.test(s)) return v; return s || undefined; };
export const bedDetails = (bedTypes = []) => (bedTypes || [])
  .filter(b => b && (b.bedType || b.quantity))
  .map(b => ({ "@type": "BedDetails", numberOfBeds: Number(b.quantity) || 1, typeOfBed: bedType(b.bedType) }));

// One room (hotel) or one whole unit (rental) → a containsPlace entry.
//
// Deliberately NOT co-typed as Product. Product requires one of offers,
// review or aggregateRating, and these nodes carry no price — room prices
// live in the page Microdata, not here — so every room would fail validation.
// Rentals use Accommodation + EntirePlace, which is the shape Google's
// vacation-rental spec asks for.
function unitNode(r, { rental, amenities }) {
  const node = {
    "@type": rental ? "Accommodation" : "HotelRoom",
    identifier: String(r.id ?? ""),
    name: r.roomName || r.name || (rental ? "Entire place" : "Room")
  };
  if (rental) node.additionalType = "EntirePlace";
  if (r.description) node.description = clip(r.description, 200);
  if (r.maxOccupancy) node.occupancy = { "@type": "QuantitativeValue", value: Number(r.maxOccupancy) };
  const beds = bedDetails(r.bedTypes);
  if (beds.length) node.bed = beds;
  if (r.roomSizeSquare) node.floorSize = { "@type": "QuantitativeValue", value: Number(r.roomSizeSquare), unitCode: /ft/i.test(r.roomSizeUnit || "") ? "FTK" : "MTK" };
  // The vacation-rental spec puts amenities on the unit, the hotel spec puts
  // them on the property. Rentals get both so either reader is satisfied.
  if (rental && amenities?.length) node.amenityFeature = amenities;
  return node;
}

// A vacation rental listing is ONE place, so rentals contribute a single
// Accommodation — the largest bookable unit, which represents the whole
// property. Hotels contribute their room catalogue.
export function roomNodes(p, { rental = false, limit = 12, amenities } = {}) {
  const rooms = (p?.rooms || []).filter(r => r && r.id != null);
  if (!rooms.length) return [];
  if (rental) {
    const best = rooms.slice().sort((a, b) => (Number(b.maxOccupancy) || 0) - (Number(a.maxOccupancy) || 0))[0];
    return [unitNode(best, { rental: true, amenities })];
  }
  return rooms.slice(0, limit).map(r => unitNode(r, { rental: false }));
}

// Full lodging entity. `p` may be the list row or the detail record.
//
// `full: false` (list rows) has no room catalogue, so such a node can never
// satisfy VacationRental's required containsPlace/occupancy and is typed
// Hotel — the rental identity still travels in additionalType and
// disambiguatingDescription. Detail pages with room data get the real
// Hotel + VacationRental typing.
export function lodgingNode(p, { id, full = true, offer } = {}) {
  const path = propertyPath(p);
  const url = absUrl(path);
  const kind = lodgingKind(p.hotelTypeId);
  const rental = kind === "rental";
  const g = geoNode(p);
  const amenities = amenityFeatures(p);
  const units = full ? roomNodes(p, { rental, amenities }) : [];
  // VacationRental requires containsPlace → occupancy → value, plus geo.
  const vacationRental = rental && !!g && units.length > 0 && Number(units[0].occupancy?.value) > 0;

  const node = {
    "@type": lodgingSchemaTypes(p.hotelTypeId, { vacationRental }),
    "@id": id || `${url}#lodging`,
    identifier: p.id,
    name: p.name,
    url,
    address: postalAddress(p)
  };
  const additional = lodgingAdditionalType(p.hotelTypeId);
  if (additional) node.additionalType = additional;
  const where = [p.area || p.city, islandDisplayFor(p)].filter(Boolean).join(", ");
  const sub = typeLabel(p.hotelTypeId);
  const showSub = sub && !["Property", "Not available", "Hotels"].includes(sub);
  node.disambiguatingDescription = `${lodgingLabel(p.hotelTypeId)}${showSub ? ` (${sub})` : ""} in ${where}, Hawaiʻi`;
  if (g) { node.geo = g; node.latitude = g.latitude; node.longitude = g.longitude; }
  // Sampled nightly band, baked in by scripts/sample-price-ranges.mjs. Unlike
  // the live offer it is present at first paint, so Search — which snapshots
  // the page before the rate call lands — and AI crawlers both see a price.
  if (p.priceRange) node.priceRange = p.priceRange;
  node.image = full ? lodgingImages(p) : lodgingImages(p, 1);
  const ar = aggregateRatingNode(p); if (ar) node.aggregateRating = ar;
  if (p.starRating > 0 && kind === "hotel") node.starRating = { "@type": "Rating", ratingValue: Number(p.starRating), bestRating: 5 };
  if (full) {
    const desc = clip(p.hotelDescription || p.description, 500); if (desc) node.description = desc;
    const times = checkinTimes(p);
    if (times.checkin) node.checkinTime = times.checkin;
    if (times.checkout) node.checkoutTime = times.checkout;
    if (amenities.length) node.amenityFeature = amenities;
    if (p.phone) node.telephone = p.phone;
    if (p.chain) node.brand = { "@type": "Brand", name: p.chain };
    if (units.length) node.containsPlace = units;
    if (p.petsAllowed != null || p.hasPetsAllowed != null) node.petsAllowed = !!(p.petsAllowed || p.hasPetsAllowed);
    node.containedInPlace = { "@type": "Place", name: islandDisplayFor(p), url: absUrl(`/${islandSlugFor(p)}/`) };
  }
  // The live price for the searched dates, when the page is showing one. It
  // mirrors the Microdata offer exactly — same total, same components — so a
  // reader that trusts either format lands on the same number.
  if (offer) node.makesOffer = offer;
  node.isPartOf = { "@id": WEBSITE_ID };
  node.provider = { "@id": ORG_ID };
  return node;
}

// Offer for a stay. `total` is the all-in figure shown on screen ("total ·
// taxes & fees included"). `lines` is the optional itemised breakdown from
// priceBreakdown() — pass it on the property page so the JSON-LD carries the
// same base-rate / tax / fee split as the Microdata.
// `checkinTime`/`checkoutTime`, when given, are already-formatted
// "YYYY-MM-DD HH:mm:ss" strings and win over the date + clock pair — the
// property page passes the same values its Microdata uses.
export function stayOfferNode({ total, checkin, checkout, checkinTime, checkoutTime, currency = "USD", lines, checkinClock = "15:00:00", checkoutClock = "11:00:00", refundable, cancelTime, name, identifier, occupancy, hotelName }) {
  if (!(total > 0)) return undefined;
  const price = Math.round(Number(total) * 100) / 100;
  const spec = { "@type": "CompoundPriceSpecification", price, priceCurrency: currency, valueAddedTaxIncluded: true };
  if (lines?.length) {
    spec.priceComponent = lines.map(l => {
      const c = { "@type": "UnitPriceSpecification", name: l.componentType || l.name, price: l.amount, priceCurrency: currency };
      if (l.componentType) c.priceComponentType = l.componentType;
      // Point of payment, same as the Microdata carries: anything collected on
      // arrival is paid to the hotel, everything else to us at booking. Without
      // it Google assumes the whole total is charged at booking, which would
      // misread a resort fee the guest actually pays at the desk.
      c.potentialAction = {
        "@type": "PayAction",
        recipient: l.atProperty
          ? { "@type": "Hotel", name: hotelName || "The property" }
          : { "@type": "OnlineBusiness", name: SITE.name }
      };
      return c;
    });
  }
  const node = {
    "@type": ["Offer", "LodgingReservation"],
    // price/priceCurrency alongside priceSpecification: the compound spec is
    // what Hotel Center's vocabulary uses, the flat pair is what generic
    // Offer readers expect. Same number either way.
    price,
    priceCurrency: currency,
    priceSpecification: spec,
    availability: "https://schema.org/InStock",
    seller: { "@id": ORG_ID }
  };
  if (name) node.name = name;
  if (identifier) node.identifier = identifier;
  const ci = checkinTime || (checkin ? `${checkin} ${checkinClock}` : null);
  const co = checkoutTime || (checkout ? `${checkout} ${checkoutClock}` : null);
  if (ci) node.checkinTime = ci;
  if (co) node.checkoutTime = co;
  if (occupancy > 0) node.occupancy = { "@type": "QuantitativeValue", value: Number(occupancy) };
  if (refundable != null) {
    node.hasMerchantReturnPolicy = refundable
      ? { "@type": "MerchantReturnPolicy", returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow", refundType: "https://schema.org/FullRefund", restockingFee: 0, ...(cancelTime ? { merchantReturnDays: cancelTime } : {}) }
      : { "@type": "MerchantReturnPolicy", returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted" };
  }
  return node;
}

// Back-compat alias for the list pages, which only ever have a total.
export const listOfferNode = stayOfferNode;

export function itemListNode(items, { id, name } = {}) {
  return {
    "@type": "ItemList",
    ...(id ? { "@id": id } : {}),
    ...(name ? { name } : {}),
    numberOfItems: items.length,
    itemListElement: items.map((item, i) => ({ "@type": "ListItem", position: i + 1, item }))
  };
}

// Editorial pages: Article + Guide. Guide is schema.org's "page that helps a
// user choose a product or service" — exactly the commerce intent we want
// Google to read into our travel guides. Never BlogPosting.
export function guideNode(a, { path } = {}) {
  const url = absUrl(path || `/${a.category}/${a.slug}/`);
  const node = {
    "@context": CONTEXT,
    "@type": ["Article", "Guide"],
    "@id": `${url}#guide`,
    mainEntityOfPage: { "@type": "WebPage", "@id": url, isPartOf: { "@id": WEBSITE_ID } },
    url,
    headline: a.title,
    name: a.title,
    description: clip(a.excerpt || a.body, 300),
    genre: "Travel guide",
    inLanguage: "en-US",
    isFamilyFriendly: true,
    author: orgRef(),
    publisher: orgRef(),
    isPartOf: { "@id": WEBSITE_ID },
    about: hawaiiPlace(),
    articleSection: a.tag || a.category,
    keywords: [a.tag, "Hawaiʻi", "hotels", "vacation rentals", "where to stay"].filter(Boolean).join(", "),
    reviewAspect: "Where to stay",
    // The commercial hook: every guide exists to help the reader book a stay.
    potentialAction: { "@type": "SearchAction", name: "Find a stay", target: absUrl("/results/") }
  };
  if (a.img) node.image = [a.img];
  // The admin worker stores dates as "Apr 28, 2026"; schema wants ISO 8601.
  const published = isoDate(a.date);
  if (published) { node.datePublished = published; node.dateModified = isoDate(a.updatedAt) || published; }
  return node;
}

export function isoDate(v) {
  if (!v) return undefined;
  if (/^\d{4}-\d{2}-\d{2}/.test(String(v))) return String(v).slice(0, 10);
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ─── Hooks ─────────────────────────────────────────────────────────────────

function setMeta(name, content) {
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!content) { el?.remove(); return; }
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setProp(prop, content) {
  let el = document.head.querySelector(`meta[property="${prop}"]`);
  if (!content) { el?.remove(); return; }
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) { el?.remove(); return; }
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

// Inject (or replace) one JSON-LD script by id. Pass null/undefined to remove.
export function useJsonLd(id, data) {
  const json = data ? JSON.stringify(data) : "";
  useEffect(() => {
    if (!json) { document.getElementById(id)?.remove(); return; }
    let el = document.getElementById(id);
    if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
    el.text = json;
    return () => { document.getElementById(id)?.remove(); };
  }, [id, json]);
}

// Title / description / canonical / robots for the current route.
export function useHead(opts) {
  const { title, description, canonical, robots } = opts || {};
  const active = !!opts;
  useEffect(() => {
    if (!active) return;
    if (title) document.title = title;
    setMeta("description", description);
    setMeta("robots", robots);
    const canon = canonical ? absUrl(canonical) : undefined;
    setLink("canonical", canon);
    // Open Graph mirrors — keeps link previews consistent with the schema.
    setProp("og:site_name", SITE.name);
    setProp("og:title", title);
    setProp("og:description", description);
    setProp("og:url", canon);
  }, [active, title, description, canonical, robots]);
}
